import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Heading,
  Skeleton,
  Text,
} from '@chakra-ui/react'
import { withSentryErrorReporting } from '@components/Fallback'
import {
  NonActiveProject,
  DataChannelConnectionLoader,
} from '@features/projects/components/WorkflowSessionPage/SessionLoaders'
import { ProjectProvider } from '@features/projects/contexts/project'
import { WorkflowSessionProvider } from '@features/projects/contexts/workflowSession'
import { useGetWorkflowSessionQuery } from '@features/sessions/api'
import { Operations } from '@lib/permissions/Operations'
import { Permission } from '@lib/permissions/Permission'
import { RESOURCE_ID } from '@lib/permissions/Resource'
import {
  createFileRoute,
  getRouteApi,
  Link,
  Outlet,
} from '@tanstack/react-router'
import { Route as listProjectsRoute } from '@routes/_auth/o/$orgId/projects'
import CheckPermissionsWrapper from '@lib/permissions/CheckPermissionsWrapper'
import { ErrorBoundary } from '@sentry/react'

const routeApi = getRouteApi(
  '/_auth/o/$orgId/projects/$projectId/$workflowSessionId'
)

const ManageSessionsFallback = ({ orgId }: { orgId: string }) => (
  <Card variant={'outline'}>
    <CardBody>
      <Heading size="md">
        There seems to be a problem loading this project.
      </Heading>
      <Text py="2">
        Try loading the page again or select another project from the list
      </Text>
    </CardBody>
    <CardFooter>
      <Link to={listProjectsRoute.to} params={{ orgId }}>
        <Button>Back to project list</Button>
      </Link>
    </CardFooter>
  </Card>
)

const projectReadPermission = new Permission(
  RESOURCE_ID.project,
  Operations.Read
)

const LiveOutputDetailRoute = () => {
  const params = routeApi.useParams()
  const { projectId, workflowSessionId, orgId } = params

  const { data, isLoading, isError } = useGetWorkflowSessionQuery({
    workflowSessionId,
  })

  const isActive = data?.status === 'active'

  return (
    <ErrorBoundary fallback={<ManageSessionsFallback orgId={orgId} />}>
      <CheckPermissionsWrapper
        permissions={[projectReadPermission]}
        height={'100%'}
        width={'100%'}
      >
        <Skeleton isLoaded={!isLoading} height={'100%'}>
          {!isActive ? (
            <NonActiveProject />
          ) : (
            <DataChannelConnectionLoader workflowSessionId={workflowSessionId}>
              <ProjectProvider projectId={projectId} format={data?.mediaFormat}>
                <WorkflowSessionProvider
                  workflowSessionId={workflowSessionId}
                  format={data?.mediaFormat}
                >
                  <Outlet />
                </WorkflowSessionProvider>
              </ProjectProvider>
            </DataChannelConnectionLoader>
          )}
        </Skeleton>
      </CheckPermissionsWrapper>
    </ErrorBoundary>
  )
}

export const Route = createFileRoute(
  '/_auth/o/$orgId/projects/$projectId/$workflowSessionId'
)({
  component: LiveOutputDetailRoute,
  beforeLoad: () => ({ getTitle: () => 'Control Room' }),
  errorComponent: withSentryErrorReporting(ManageSessionsFallback),
})
